// Phase 9.0 — Locations management page.
//
// Phase 9.7.7 rewrite — replaces the per-row Save button with a
// page-level `pendingEdits` bag + sticky bottom "Save all changes"
// bar. Every hover-card / input edit updates the bag; the sticky
// bar fires ONE POST /api/fnb/locations/bulk-patch on submit.
//
// Design notes for anyone touching this file:
//   • `pendingEdits` is keyed by `location_id` and lives on the page
//     component so it PERSISTS across filter / sort / search
//     re-renders (spec §1). Do NOT reset it inside effects that fire
//     on filter changes.
//   • `LocationRow` is now a purely-controlled component — it reads
//     the effective field values from `loc` MERGED with
//     `pendingEdits[loc.location_id]` and writes back through the
//     `onFieldChange(loc_id, field, value)` prop.
//   • A row that has any pending edits shows an amber dot + chip
//     next to the slug (`data-testid="loc-row-unsaved-<loc_id>"`).
//   • `beforeunload` + React Router `useBlocker` prompt on nav-away
//     when the pending bag is non-empty.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Switch } from "@/components/ui/switch";
import { HelpCircle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Every field the bulk-patch endpoint accepts — used to project user
// edits into a clean patch payload (and to compute the diff vs. the
// server-side row when the user hits Save all).
const PATCHABLE_FIELDS = [
  "display_name", "stock_column", "gstin", "state_code", "address",
  "phone", "city", "state", "pincode", "company_name",
  // Phase 9.7.9 — POS "Store Code" (MLR/MPP/CT/GUR/…). Editing
  // stamps `store_code_source='manual'` so the next backfill run
  // skips this row.
  "store_code",
  "is_po_target", "is_warehouse", "active",
];

function SourceBadge({ source }) {
  const label = source || "auto";
  const variant = label === "seed" ? "default" : label === "manual" ? "secondary" : "outline";
  return (
    <Badge variant={variant} className="text-[10px] uppercase tracking-wider" data-testid={`loc-source-${label}`}>
      {label}
    </Badge>
  );
}

function StockColumnSelect({ value, options, onChange, testid }) {
  return (
    <Select
      value={value || "__none__"}
      onValueChange={(v) => onChange(v === "__none__" ? null : v)}
    >
      <SelectTrigger className="min-w-[260px] text-xs font-mono" data-testid={testid}>
        <SelectValue placeholder="Pick a column…" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">(none)</SelectItem>
        {options.map((c) => (
          <SelectItem key={c} value={c} className="font-mono text-xs">{c}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NewLocationRow({ knownStockColumns, onCancel, onCreate }) {
  const [draft, setDraft] = useState({
    location_id: "",
    display_name: "",
    stock_column: null,
    gstin: "",
    state_code: "",
    address: "",
    is_po_target: true,
    is_warehouse: false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setDraft((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!draft.location_id || !draft.display_name) {
      toast.error("Slug and Display Name are required");
      return;
    }
    setSaving(true);
    try {
      await onCreate(draft);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow data-testid="new-location-row">
      <TableCell className="w-8 px-2">—</TableCell>
      <TableCell>
        <Input
          value={draft.location_id}
          onChange={(e) => set("location_id", e.target.value)}
          placeholder="gostops-xxx"
          className="font-mono text-xs w-40"
          data-testid="new-loc-slug"
        />
      </TableCell>
      <TableCell>
        <Input
          value={draft.display_name}
          onChange={(e) => set("display_name", e.target.value)}
          placeholder="Display name"
          className="text-xs w-56"
          data-testid="new-loc-display-name"
        />
      </TableCell>
      <TableCell>
        <StockColumnSelect
          value={draft.stock_column}
          options={knownStockColumns}
          onChange={(v) => set("stock_column", v)}
          testid="new-loc-stock-column"
        />
      </TableCell>
      <TableCell>
        <Input
          value={draft.gstin}
          onChange={(e) => set("gstin", e.target.value)}
          placeholder="GSTIN"
          className="font-mono text-xs w-40"
          data-testid="new-loc-gstin"
        />
      </TableCell>
      <TableCell>
        <Input
          value={draft.state_code}
          onChange={(e) => set("state_code", e.target.value)}
          placeholder="07"
          className="font-mono text-xs w-12"
          data-testid="new-loc-state"
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={draft.is_warehouse}
          onCheckedChange={(v) => set("is_warehouse", v)}
          data-testid="new-loc-warehouse"
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={draft.is_po_target}
          onCheckedChange={(v) => set("is_po_target", v)}
          data-testid="new-loc-po-target"
        />
      </TableCell>
      <TableCell>—</TableCell>
      <TableCell className="text-right space-x-2">
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="new-loc-cancel">
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={saving} data-testid="new-loc-save">
          {saving ? "Saving…" : "Create"}
        </Button>
      </TableCell>
    </TableRow>
  );
}

// Controlled row. Reads `field(loc, k)` for the effective value
// (merging pending edits over the server-side row) and writes through
// `onFieldChange(loc_id, k, v)`.
function LocationRow({
  loc, allStockColumns, selected, onToggleSelect,
  field, onFieldChange, hasPending,
}) {
  // Phase 9.7.1b — visual disambiguation for `is_active` (the manual
  // override set by Dashboard tile-toggle + bulk-active endpoint).
  const manuallyInactive = loc.is_active === false;

  return (
    <TableRow
      data-testid={`location-row-${loc.location_id}`}
      data-manual-inactive={manuallyInactive ? "true" : "false"}
      className={manuallyInactive ? "bg-muted/30 text-muted-foreground" : undefined}
    >
      <TableCell className="w-8 px-2">
        <Checkbox
          checked={selected}
          onCheckedChange={() => onToggleSelect(loc.location_id)}
          data-testid={`loc-select-${loc.location_id}`}
        />
      </TableCell>
      <TableCell className="font-mono text-[11px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span>{loc.location_id}</span>
          <SourceBadge source={loc.source} />
          {hasPending && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800"
              data-testid={`loc-row-unsaved-${loc.location_id}`}
              title="This row has unsaved changes. Use the Save-all bar at the bottom."
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              unsaved
            </span>
          )}
          {manuallyInactive && (
            <Badge
              variant="outline"
              className="text-[10px] border-rose-300 text-rose-700 bg-rose-50"
              data-testid={`loc-manual-inactive-badge-${loc.location_id}`}
              title="Manually inactivated via Dashboard tile or bulk action bar"
            >
              MANUAL: INACTIVE
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Input
          value={field(loc, "display_name") || ""}
          onChange={(e) => onFieldChange(loc.location_id, "display_name", e.target.value)}
          className="text-xs w-56"
          data-testid={`loc-display-name-${loc.location_id}`}
        />
      </TableCell>
      {/* Phase 9.7.9 — POS store code. Feeds the multidate-activity
          matcher's first-pass. Hand edits stamp `store_code_source=manual`
          on the server which protects the row from future backfills. */}
      <TableCell>
        <Input
          value={field(loc, "store_code") || ""}
          onChange={(e) => onFieldChange(loc.location_id, "store_code", (e.target.value || "").toUpperCase())}
          className="font-mono uppercase text-xs w-20"
          placeholder="—"
          data-testid={`loc-store-code-${loc.location_id}`}
        />
        {loc.store_code_source === "manual" && (
          <div className="text-[9px] text-amber-700 mt-0.5">manual-locked</div>
        )}
      </TableCell>
      <TableCell>
        <StockColumnSelect
          value={field(loc, "stock_column")}
          options={allStockColumns}
          onChange={(v) => onFieldChange(loc.location_id, "stock_column", v)}
          testid={`loc-stock-column-${loc.location_id}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={field(loc, "gstin") || ""}
          onChange={(e) => onFieldChange(loc.location_id, "gstin", e.target.value)}
          className="font-mono text-xs w-40"
          data-testid={`loc-gstin-${loc.location_id}`}
        />
        {loc.gstin_source === "manual" && (
          <div className="text-[9px] text-amber-700 mt-0.5">manual-locked</div>
        )}
      </TableCell>
      <TableCell>
        <Input
          value={field(loc, "state_code") || ""}
          onChange={(e) => onFieldChange(loc.location_id, "state_code", e.target.value)}
          className="font-mono text-xs w-12"
          data-testid={`loc-state-${loc.location_id}`}
        />
      </TableCell>
      {/* Phase 9.7.5 — Address / Phone / City columns seeded from
          `PO Ordering.xlsx`. Address gets a hover-card for the full
          text since it's long; city/phone stay inline. */}
      <TableCell>
        <HoverCard openDelay={200}>
          <HoverCardTrigger asChild>
            <div
              className="text-[11px] max-w-[220px] truncate cursor-help text-muted-foreground"
              data-testid={`loc-address-preview-${loc.location_id}`}
            >
              {field(loc, "address") || <span className="italic">—</span>}
            </div>
          </HoverCardTrigger>
          <HoverCardContent side="top" align="start" className="w-96 text-xs">
            <div className="font-medium mb-1">Address (editable)</div>
            <Textarea
              value={field(loc, "address") || ""}
              onChange={(e) => onFieldChange(loc.location_id, "address", e.target.value)}
              rows={4}
              className="text-xs"
              data-testid={`loc-address-input-${loc.location_id}`}
            />
            {loc.address_source && (
              <div className="text-[10px] text-muted-foreground mt-1">
                Source: <span className={loc.address_source === "manual" ? "text-amber-700 font-medium" : ""}>{loc.address_source}</span>
              </div>
            )}
          </HoverCardContent>
        </HoverCard>
      </TableCell>
      <TableCell>
        <Input
          value={field(loc, "phone") || ""}
          onChange={(e) => onFieldChange(loc.location_id, "phone", e.target.value)}
          className="font-mono text-xs w-32"
          data-testid={`loc-phone-${loc.location_id}`}
        />
      </TableCell>
      <TableCell>
        <Input
          value={field(loc, "city") || ""}
          onChange={(e) => onFieldChange(loc.location_id, "city", e.target.value)}
          className="text-xs w-24"
          data-testid={`loc-city-${loc.location_id}`}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={!!field(loc, "is_warehouse")}
          onCheckedChange={(v) => onFieldChange(loc.location_id, "is_warehouse", v)}
          data-testid={`loc-warehouse-${loc.location_id}`}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={!!field(loc, "is_po_target")}
          onCheckedChange={(v) => onFieldChange(loc.location_id, "is_po_target", v)}
          data-testid={`loc-po-target-${loc.location_id}`}
        />
      </TableCell>
      <TableCell>
        <Switch
          checked={!!field(loc, "active")}
          onCheckedChange={(v) => onFieldChange(loc.location_id, "active", v)}
          data-testid={`loc-active-${loc.location_id}`}
        />
      </TableCell>
    </TableRow>
  );
}

export default function FnbPoLocations() {
  const [locations, setLocations] = useState([]);
  const [orphanCols, setOrphanCols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("");
  // Phase 9.7.1b — bulk selection state.
  const [selected, setSelected] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);

  // Phase 9.7.7 — page-level pending edits bag. Keyed by location_id.
  // Each value is a partial dict of field → new value. NEVER reset
  // on filter/sort/search re-renders.
  const [pendingEdits, setPendingEdits] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const pendingCount = Object.keys(pendingEdits).length;
  const hasPending = pendingCount > 0;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [locRes, statusRes] = await Promise.all([
        api.get("/fnb/locations"),
        api.get("/fnb/master-data/status"),
      ]);
      setLocations(locRes.data);
      setOrphanCols(statusRes.data.orphan_stock_columns || []);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Pool of every stock column we've seen anywhere.
  const allStockColumns = useMemo(() => {
    const s = new Set();
    locations.forEach((l) => { if (l.stock_column) s.add(l.stock_column); });
    orphanCols.forEach((c) => s.add(c));
    return Array.from(s).sort();
  }, [locations, orphanCols]);

  // Effective-value getter: pending edit wins over the server value.
  const field = useCallback((loc, key) => {
    const p = pendingEdits[loc.location_id];
    if (p && Object.prototype.hasOwnProperty.call(p, key)) return p[key];
    return loc[key];
  }, [pendingEdits]);

  // Add / update a single field. If the new value matches the
  // server row, DROP the pending entry (keeps the bag exact so
  // "unsaved" chips are honest about what actually changed).
  const onFieldChange = useCallback((locId, key, value) => {
    setPendingEdits((prev) => {
      const server = locations.find((l) => l.location_id === locId);
      const next = { ...prev };
      const row = { ...(next[locId] || {}) };
      // A tri-state "unset stock_column" case: null must be captured
      // explicitly because it also happens to be a plausible server
      // value.
      const same = server && server[key] === value;
      if (same) {
        delete row[key];
      } else {
        row[key] = value;
      }
      if (Object.keys(row).length === 0) {
        delete next[locId];
      } else {
        next[locId] = row;
      }
      return next;
    });
  }, [locations]);

  const onCreate = useCallback(
    async (draft) => {
      try {
        await api.post("/fnb/locations", draft);
        toast.success(`Created ${draft.location_id}`);
        setCreating(false);
        await reload();
      } catch (e) {
        toast.error(detailFromError(e));
      }
    },
    [reload],
  );

  const filtered = useMemo(() => {
    if (!filter) return locations;
    const f = filter.toLowerCase();
    return locations.filter(
      (l) =>
        l.location_id.toLowerCase().includes(f)
        || (l.display_name || "").toLowerCase().includes(f)
        || (l.stock_column || "").toLowerCase().includes(f),
    );
  }, [locations, filter]);

  // Phase 9.7.1b — bulk selection & bulk-active handlers.
  const toggleSelect = useCallback((locId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(locId)) next.delete(locId);
      else next.add(locId);
      return next;
    });
  }, []);
  const filteredIds = useMemo(() => filtered.map((l) => l.location_id), [filtered]);
  const allVisibleSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const toggleAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, [allVisibleSelected, filteredIds]);
  const clearSelection = () => setSelected(new Set());

  const bulkActive = useCallback(async (isActive) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkSaving(true);
    try {
      const res = await api.post("/fnb/locations/bulk-active", {
        location_ids: ids,
        is_active: isActive,
      });
      toast.success(
        `${res.data.modified} of ${ids.length} location${ids.length === 1 ? "" : "s"} ` +
        `marked ${isActive ? "active" : "inactive"}`,
      );
      clearSelection();
      await reload();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setBulkSaving(false);
    }
  }, [selected, reload]);

  // Phase 9.7.7 — Save all — submit ONE bulk-patch request.
  const saveAll = useCallback(async () => {
    if (!hasPending || savingAll) return;
    // Project the bag into `{updates: [{location_id, patch}]}` and
    // add `clear_stock_column=true` when a user explicitly nulled the
    // stock_column value (backend needs this hint because JSON null
    // is indistinguishable from missing key).
    const updates = Object.entries(pendingEdits).map(([location_id, patch]) => {
      const clean = {};
      for (const k of PATCHABLE_FIELDS) {
        if (Object.prototype.hasOwnProperty.call(patch, k)) clean[k] = patch[k];
      }
      if (Object.prototype.hasOwnProperty.call(patch, "stock_column")
          && (patch.stock_column === null || patch.stock_column === undefined)) {
        clean.clear_stock_column = true;
      }
      return { location_id, patch: clean };
    });
    setSavingAll(true);
    try {
      const res = await api.post("/fnb/locations/bulk-patch", { updates });
      const ok = (res.data.updated || []).length;
      const failed = res.data.failed || [];
      if (failed.length === 0) {
        toast.success(`Saved ${ok} location${ok === 1 ? "" : "s"}`);
        setPendingEdits({});
      } else {
        // Partial success: keep the failed rows in the bag so the
        // operator can retry them. Drop the ones that succeeded.
        const failedIds = new Set(failed.map((f) => f.location_id));
        setPendingEdits((prev) => {
          const next = {};
          for (const [lid, p] of Object.entries(prev)) {
            if (failedIds.has(lid)) next[lid] = p;
          }
          return next;
        });
        toast.error(
          `Saved ${ok}, ${failed.length} failed: `
          + failed.slice(0, 3).map((f) => `${f.location_id} (${f.error})`).join(", ")
          + (failed.length > 3 ? `, +${failed.length - 3} more` : ""),
        );
      }
      await reload();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setSavingAll(false);
    }
  }, [hasPending, savingAll, pendingEdits, reload]);

  const discardAll = useCallback(() => {
    setPendingEdits({});
    setShowDiscardConfirm(false);
    toast.success("Discarded all unsaved changes");
  }, []);

  // Phase 9.7.7 — browser-level guard: prevent close/reload with
  // unsaved changes. Standard `beforeunload` — the string message
  // is ignored by modern browsers but the confirmation is shown.
  // NOTE: React Router `useBlocker` isn't available under the app's
  // declarative <BrowserRouter> setup, so in-app SPA navigation
  // relies on the amber sticky bar for visibility. If we ever
  // migrate to `createBrowserRouter`, wire useBlocker here.
  useEffect(() => {
    if (!hasPending) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasPending]);

  return (
    <div className="space-y-6" data-testid="fnb-po-locations">
      <header className="flex items-center justify-between">
        <div className="space-y-1">
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="locations-heading"
          >
            Locations
          </h1>
          <p className="text-sm text-muted-foreground">
            Every stocking + selling location the system knows about. Edit inline; changes queue up in the bottom bar — click <b>Save all</b> to commit.
          </p>
        </div>
        <Button asChild variant="ghost" size="sm" data-testid="fnb-locations-back">
          <Link
            to="/tools/fnb-po"
            onClick={(e) => {
              // In-page guard for the one Link we control; the sticky
              // bar covers the other exits visually.
              if (hasPending) {
                const ok = window.confirm(
                  `You have unsaved changes on ${pendingCount} location`
                  + `${pendingCount === 1 ? "" : "s"}.\n\nLeave the page and lose them?`,
                );
                if (!ok) e.preventDefault();
              }
            }}
          >← Dashboard</Link>
        </Button>
      </header>

      {selected.size > 0 && (
        <div
          className="sticky top-0 z-20 flex items-center gap-3 rounded-md border border-border/70 bg-background/95 backdrop-blur px-4 py-2.5 shadow-sm"
          data-testid="loc-bulk-action-bar"
        >
          <span className="text-sm font-medium" data-testid="loc-bulk-selected-count">
            {selected.size} selected
          </span>
          <div className="h-4 w-px bg-border/70" />
          <Button
            size="sm"
            onClick={() => bulkActive(true)}
            disabled={bulkSaving}
            data-testid="loc-bulk-mark-active"
          >
            {bulkSaving ? "…" : "Mark Active"}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => bulkActive(false)}
            disabled={bulkSaving}
            data-testid="loc-bulk-mark-inactive"
          >
            {bulkSaving ? "…" : "Mark Inactive"}
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="ghost"
            onClick={clearSelection}
            disabled={bulkSaving}
            data-testid="loc-bulk-cancel"
          >
            Cancel
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">
              {locations.length} location{locations.length === 1 ? "" : "s"}
            </CardTitle>
            <Button
              size="sm"
              onClick={() => setCreating((v) => !v)}
              data-testid="new-location-button"
            >
              {creating ? "Cancel" : "+ New Location"}
            </Button>
          </div>
          <CardDescription>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by slug, display name or stock column…"
              className="max-w-md"
              data-testid="locations-filter"
            />
          </CardDescription>
        </CardHeader>
        <CardContent className={hasPending ? "pb-24" : undefined}>
          {loading ? (
            <div className="text-sm text-muted-foreground" data-testid="locations-loading">
              Loading…
            </div>
          ) : (
            <Table data-testid="locations-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8 px-2">
                    <Checkbox
                      checked={allVisibleSelected}
                      onCheckedChange={toggleAllVisible}
                      data-testid="loc-select-all"
                      disabled={filteredIds.length === 0}
                    />
                  </TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Stock Column</TableHead>
                  <TableHead>GSTIN</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>PO Target</TableHead>
                  <TableHead>
                    <div className="inline-flex items-center gap-1">
                      Detected
                      <HoverCard openDelay={100}>
                        <HoverCardTrigger asChild>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground focus:outline-none"
                            data-testid="loc-detected-help"
                            aria-label="What does Detected mean?"
                          >
                            <HelpCircle className="w-3.5 h-3.5" />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="top"
                          align="end"
                          className="text-xs w-72 leading-relaxed"
                          data-testid="loc-detected-help-content"
                        >
                          This flag is set automatically when the outlet
                          appears in the latest stock CSV upload. To
                          manually take an outlet offline for procurement,
                          use the row checkbox + <b>Mark Inactive</b> above.
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {creating && (
                  <NewLocationRow
                    knownStockColumns={allStockColumns}
                    onCancel={() => setCreating(false)}
                    onCreate={onCreate}
                  />
                )}
                {filtered.map((loc) => (
                  <LocationRow
                    key={loc.location_id}
                    loc={loc}
                    allStockColumns={allStockColumns}
                    selected={selected.has(loc.location_id)}
                    onToggleSelect={toggleSelect}
                    field={field}
                    onFieldChange={onFieldChange}
                    hasPending={!!pendingEdits[loc.location_id]}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Phase 9.7.7 — Sticky bottom bar */}
      {hasPending && (
        <div
          className="fixed inset-x-0 bottom-0 z-30 border-t border-amber-200 bg-amber-50 shadow-[0_-4px_16px_-8px_rgba(0,0,0,0.15)]"
          data-testid="loc-unsaved-bar"
        >
          {/* Phase 9.7.7 UX fix — three-column layout so the action
              buttons sit at the TRUE horizontal center of the bar,
              regardless of viewport width. The right spacer also
              guarantees the buttons never collide with the fixed
              "Made with Emergent" badge (~200px × 40px, bottom-right)
              in the preview environment. `pr-24` on the outer padding
              adds a hard floor of extra room on narrower widths. */}
          <div className="mx-auto max-w-screen-2xl px-4 pr-24 py-3 flex items-center gap-4">
            <div className="flex-1 min-w-0 flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse shrink-0" aria-hidden="true" />
              <span className="font-medium text-amber-900 truncate" data-testid="loc-unsaved-count">
                {pendingCount} location{pendingCount === 1 ? "" : "s"} {pendingCount === 1 ? "has" : "have"} unsaved changes
              </span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowDiscardConfirm(true)}
                disabled={savingAll}
                data-testid="loc-discard-all"
                className="text-amber-900 hover:bg-amber-100"
              >
                Discard all
              </Button>
              <Button
                size="sm"
                onClick={saveAll}
                disabled={savingAll}
                data-testid="loc-save-all"
                className="bg-amber-600 hover:bg-amber-700"
              >
                {savingAll ? "Saving…" : `Save all (${pendingCount})`}
              </Button>
            </div>
            {/* Right spacer — balances the count column so the button
                group is visually centered. Hidden below sm since the
                bar collapses to two columns on narrow screens. */}
            <div className="flex-1 min-w-0 hidden sm:block" aria-hidden="true" />
          </div>
        </div>
      )}

      {showDiscardConfirm && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center bg-background/80 backdrop-blur-sm"
          data-testid="loc-discard-modal"
        >
          <Card className="max-w-md m-4">
            <CardHeader>
              <CardTitle className="text-base">
                Discard {pendingCount} unsaved change{pendingCount === 1 ? "" : "s"}?
              </CardTitle>
              <CardDescription>
                Every pending edit on the {pendingCount} location{pendingCount === 1 ? "" : "s"} will be reverted.
                This cannot be undone.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setShowDiscardConfirm(false)}
                data-testid="loc-discard-cancel"
              >
                Keep editing
              </Button>
              <Button
                variant="destructive"
                onClick={discardAll}
                data-testid="loc-discard-confirm"
              >
                Discard all
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
